import { useEffect, useMemo, useState } from "react";
import api, { fmtDT } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Printer, Search, FileText } from "lucide-react";
import PrintBundle from "@/components/PrintBundle";

/**
 * Print Center — search any OPD and print any combination of Rx / Investigation
 * Slip / OPD Bill (A4 / 58mm / 80mm) as a single batch. Also lists shortcuts to
 * the other print modules (Discharge, Payslip, Reports) which live on their own
 * screens.
 */
export default function PrintCenter() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null); // OPD row selected for bundle

  useEffect(() => {
    api.get("/opd", { params: { limit: 100 } }).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows.slice(0, 25);
    return rows.filter((r) =>
      (r.opd_no || "").toLowerCase().includes(term) ||
      (r.uhid || "").toLowerCase().includes(term) ||
      (r.patient_name || "").toLowerCase().includes(term) ||
      (r.doctor_name || "").toLowerCase().includes(term)
    ).slice(0, 25);
  }, [q, rows]);

  const otherPrints = [
    { label: "Discharge Summary (A4)", note: "Open an IPD → Discharge tab → Print." },
    { label: "Payslip", note: "Open HRMS → Payroll → row → Print." },
    { label: "Reports", note: "Open Reports → Filter → Print." },
    { label: "Investigation Requisition", note: "Pick the OPD below and tick Investigation Slip." },
    { label: "UHID / Patient Sticker", note: "Open the patient profile → Print sticker." },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-semibold">Print Center</h1>
        <p className="text-sm text-slate-500">Pick an OPD and tick the slips you want — print them all in one go.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left · OPD search + list */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400"/>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by OPD # / UHID / patient / doctor" className="pl-7 h-9" data-testid="pc-search" />
            </div>
            <div className="text-xs text-slate-500">Recent 25</div>
          </div>
          <div className="border border-slate-200 max-h-[380px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left label-tiny px-2 py-1.5">OPD #</th>
                  <th className="text-left label-tiny px-2 py-1.5">Patient</th>
                  <th className="text-left label-tiny px-2 py-1.5">Doctor</th>
                  <th className="text-left label-tiny px-2 py-1.5">Date</th>
                  <th className="text-right label-tiny px-2 py-1.5 w-20">Pick</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isSel = selected?.id === r.id;
                  return (
                    <tr key={r.id} data-testid={`pc-row-${r.opd_no}`} className={`border-t cursor-pointer ${isSel ? "bg-blue-50" : "hover:bg-slate-50"}`} onClick={() => setSelected(r)}>
                      <td className="px-2 py-1.5 font-mono text-xs">{r.opd_no}</td>
                      <td className="px-2 py-1.5">
                        <div className="font-medium">{r.patient_name}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{r.uhid}</div>
                      </td>
                      <td className="px-2 py-1.5 text-xs">{r.doctor_name}</td>
                      <td className="px-2 py-1.5 text-xs text-slate-500">{fmtDT(r.created_at)}</td>
                      <td className="px-2 py-1.5 text-right">
                        {isSel ? <Badge className="bg-blue-700 text-white rounded-sm">Selected</Badge> : <span className="text-[11px] text-blue-700 underline">Pick</span>}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-xs text-slate-500 py-4">No OPDs match — try a different search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right · Bundle picker */}
        <div className="space-y-3">
          {selected ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                <div className="text-[10px] tracking-widest font-semibold text-blue-800">SELECTED OPD</div>
                <div className="font-medium mt-1">{selected.patient_name}</div>
                <div className="text-xs text-slate-600 font-mono">{selected.opd_no} · {selected.uhid}</div>
                <div className="text-xs text-slate-600">{selected.doctor_name}</div>
              </div>
              <PrintBundle
                opdId={selected.id}
                title="Bundle Picker"
                subtitle="Tick documents · Preview or Print"
              />
            </>
          ) : (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-sm p-6 text-center">
              <Printer className="w-6 h-6 text-slate-400 mx-auto mb-2" />
              <div className="text-sm text-slate-600 font-medium">Pick an OPD from the list</div>
              <div className="text-[11px] text-slate-500 mt-1">Then tick Rx / Investigation Slip / OPD Bill and hit Print.</div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="label-tiny mb-2">Other Print Modules</div>
            <ul className="space-y-1.5">
              {otherPrints.map((p) => (
                <li key={p.label} className="flex items-start gap-2 text-xs">
                  <FileText className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-800">{p.label}</div>
                    <div className="text-[11px] text-slate-500">{p.note}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
